import { Button, Col, Form, List, Row, theme, Typography } from "antd";
import { useEffect, useState } from "react";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { NotificationForm } from "./NotificationFormDialog.default";
import {
  addNotification,
  deleteNotification,
  updateNotification,
  getCompanies,
  getCompanyDepartments,
  getPlatformUsers,
  getNotificationRecipients,
} from "../repository/data.repository";
import { getProblemMessage, retry } from "../utils/http";
import { MAX_PAGE_SIZE, NotificationType } from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../hooks/useSearchRequest";
import DynamicForm from "../controls/DynamicForm";
import { InputObject } from "../controls/default";
import _ from "lodash";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import dayjs from "dayjs";
import InModal from "../controls/InModal";

export interface NotificationFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly initialValues?: NotificationForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function NotificationFormDialog(props: NotificationFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<NotificationForm>([], form);
  const [targetForm] = Form.useForm();
  const targetValues = Form.useWatch<{
    readonly target: InputObject;
  }>([], targetForm);
  const [targets, setTargets] = useState<InputObject[]>([]);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (props.open) {
      if (props.initialValues?.uid) {
        setDataLoading(true);
        retry(() =>
          getNotificationRecipients({
            pageNumber: 0,
            pageSize: MAX_PAGE_SIZE,
            searchQuery: {
              conditions: [
                {
                  field: "notificationUid",
                  operation: QueryOperation.Equal,
                  values: [props.initialValues?.uid!],
                },
              ],
            },
          }),
        ).then((result) => {
          setDataLoading(false);
          targetForm.resetFields();
          form.setFieldsValue(props.initialValues);
          const targets = result.data.map((item) => ({
            display: item.userUserName,
            value: item.userUid,
            dataItem: { ...item, __name: "User", __type: "User" },
          }));
          setTargets(targets);
        });
      } else {
        targetForm.resetFields();
        form.setFieldsValue(props.initialValues);
        setTargets([]);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  // Events

  function onAddTarget(): void {
    const { target } = targetValues || {};

    if (target && target.dataItem) {
      const updateTargets = _.uniqBy(
        [...targets, target],
        (item) => `${item.dataItem.__type}-${item.value}`,
      );
      setTargets(updateTargets);
      // Keep the Company / Department scope; only clear the picked recipient.
      targetForm.resetFields(["target"]);
    }
  }

  function onRemoveTarget(index: number): void {
    const filterTargets = targets.filter((_, i) => i !== index);
    setTargets(filterTargets);
  }

  function onSubmit(): void {
    const { title, message, type, scheduledAt } = values;

    setLoading(true);
    setErrorMessage("");

    const mappedTargets = targets.map((item) => ({
      targetType: item.dataItem.__type,
      targetUid: item.value,
    }));

    const onError = (error: any) => {
      setLoading(false);
      setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
    };

    if (props.mode === "add") {
      addNotification({
        title,
        message,
        type: type.value,
        scheduledAt: dayjs(scheduledAt).toDate(),
        targets: mappedTargets,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateNotification({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        title,
        message,
        scheduledAt: dayjs(scheduledAt).toDate(),
        targets: mappedTargets,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteNotification(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage("");
    setLoading(false);
    form.resetFields();
    targetForm.resetFields();
    setTargets([]);
  }

  // Recipient picker: grouped Companies / Departments / Users, soft-scoped by the chosen
  // Company / Department (role / label / tenant targeting is deprecated here).
  const targetRequest = (search: string) => {
    const companyUid = targetForm.getFieldValue("scopeCompany")?.value;
    const departmentUid = targetForm.getFieldValue("scopeDepartment")?.value;

    const nameCondition = {
      field: "name",
      operation: QueryOperation.StartsWith,
      values: [search],
    };
    const scopeCondition = (field: string, value?: string) =>
      value
        ? [{ field, operation: QueryOperation.Equal, values: [value] }]
        : [];

    return retry(() =>
      Promise.all([
        getCompanies({
          searchQuery: { conditions: [nameCondition] },
          pageNumber: 0,
          pageSize: 50,
        }).then(({ data }) =>
          data.map((dataItem) => ({
            display: dataItem.name,
            value: dataItem.uid,
            dataItem: { ...dataItem, __name: "Company", __type: "Company" },
          })),
        ),
        getCompanyDepartments({
          searchQuery: {
            conditions: [nameCondition, ...scopeCondition("companyUid", companyUid)],
          },
          pageNumber: 0,
          pageSize: 50,
        }).then(({ data }) =>
          data.map((dataItem) => ({
            display: dataItem.name,
            subDisplay: [dataItem.companyName].filter((f) => !!f).join(" / "),
            value: dataItem.uid,
            dataItem: { ...dataItem, __name: "Department", __type: "Department" },
          })),
        ),
        getPlatformUsers({
          searchQuery: {
            conditions: [
              { field: "userName", operation: QueryOperation.StartsWith, values: [search] },
              ...scopeCondition("companyUid", companyUid),
              ...scopeCondition("departmentUid", departmentUid),
            ],
          },
          pageNumber: 0,
          pageSize: 50,
        }).then(({ data }) =>
          data.map((dataItem) => ({
            display: [dataItem.firstName, dataItem.lastName].join(" "),
            subDisplay: [dataItem.companyName, dataItem.departmentName]
              .filter((f) => !!f)
              .join(" / "),
            value: dataItem.uid,
            dataItem: { ...dataItem, __name: "User", __type: "User" },
          })),
        ),
      ]),
    ).then(([companies, departments, users]) => [
      { display: "Companies", value: companies },
      { display: "Departments", value: departments },
      { display: "Users", value: users },
    ]);
  };

  return (
    <InModal
      open={props.open}
      loading={dataLoading}
      loadingLabel="Loading notification…"
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Notification", props.initialValues?.title)}
      onCancel={onCancel}
      actions={
        props.mode != "view" && (
          <ModalFooter
            mode={props.mode}
            loading={loading}
            valid={valid}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        )
      }
    >
      {props.mode !== "delete" && (
        <Row className="mb-4 flex flex-row">
          <Col className="flex flex-1 flex-col">
            <DynamicForm
              className="w-full"
              form={form}
              preview={props.mode === "view"}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "combobox",
                        name: "type",
                        label: "Type",
                        disabled: props.mode === "update",
                        rules: [
                          {
                            required: props.mode === "add",
                            message: "Type is required",
                          },
                        ],
                        request: () =>
                          Promise.resolve(
                            Object.entries(NotificationType).map(
                              ([value, display]) => ({
                                value,
                                display,
                              }),
                            ),
                          ),
                      },
                      {
                        type: "textbox",
                        name: "title",
                        label: "Title",
                        rules: [
                          {
                            required: true,
                            message: "Title is required",
                          },
                        ],
                      },
                      {
                        type: "datetimepicker",
                        name: "scheduledAt",
                        label: "Scheduled At",
                      },
                      {
                        type: "areabox",
                        name: "message",
                        label: "Message",
                        rows: 5,
                        rules: [
                          {
                            required: true,
                            message: "Message is required",
                          },
                        ],
                      },
                    ],
                  },
                ],
              }}
            />
          </Col>
          <Col className="flex flex-1 flex-col">
            {props.mode !== "view" && (
              <DynamicForm
                className="w-full"
                form={targetForm}
                schema={{
                  groups: [
                    {
                      controls: [
                        {
                          type: "combobox",
                          label: "Scope: Company",
                          name: "scopeCompany",
                          request: createSearchRequest(getCompanies, "name"),
                        },
                        {
                          type: "combobox",
                          label: "Scope: Department",
                          name: "scopeDepartment",
                          dependsOn: ["scopeCompany"],
                          request: createScopedFilterRequest(
                            getCompanyDepartments,
                            "name",
                            () => [
                              [
                                "companyUid",
                                targetForm.getFieldValue("scopeCompany")?.value,
                              ],
                            ],
                          ),
                        },
                        {
                          type: "combobox",
                          label: "Add recipient",
                          name: "target",
                          dependsOn: ["scopeCompany", "scopeDepartment"],
                          request: targetRequest,
                        },
                      ],
                    },
                  ],
                }}
                onChange={onAddTarget}
              />
            )}
            {!!targets.length && (
              <List
                className="ml-1 mr-1 mt-2"
                style={{
                  backgroundColor: token.colorBgLayout,
                  borderRadius: token.borderRadius,
                }}
                dataSource={targets}
                renderItem={(item, index) => {
                  return (
                    <List.Item className="p-1 pl-2 pr-2">
                      <Col className="flex flex-auto flex-col">
                        <Typography.Text>{item.display}</Typography.Text>
                        <Typography.Text
                          style={{
                            color: token.colorTextDescription,
                          }}
                        >
                          {item.dataItem?.__name}
                        </Typography.Text>
                      </Col>
                      {props.mode !== "view" && (
                        <Button
                          type={"text"}
                          icon={
                            <Icon
                              className="-ml-0.5 text-xl"
                              component={Icons.CloseIcon}
                            />
                          }
                          onClick={() => onRemoveTarget(index)}
                        />
                      )}
                    </List.Item>
                  );
                }}
              />
            )}
          </Col>
        </Row>
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="notification"
          entityName={props.initialValues?.title}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default NotificationFormDialog;
